import React, {useState, useEffect, useContext} from 'react';
import ShopifyClient from 'shopify-buy/index.unoptimized.umd';
import cookie from 'js-cookie';

const SHOPIFY_CHECKOUT_STORAGE_KEY = 'shopify_checkout_id';

const client = ShopifyClient.buildClient({
	storefrontAccessToken: process.env.GATSBY_SHOPIFY_TOKEN,
	domain: process.env.GATSBY_SHOPIFY_STORE,
});

const initialStoreState = {
	client,
	isAdding: false,
	cartIsOpen: false,
	menuIsOpen: false,
	submenuIsOpen: false,
	activeSubmenu: undefined,
	searchIsOpen: false,
	noticeBarEnabled: false,
	noticeBarVisible: false,
	noticeBarDismissed: false,
	noticeBarHeight: 0,
	page: undefined,
	customerEmail: undefined,
	customerName: undefined,
	customerToken: undefined,
	orders: [],
	checkout: {
		lineItems: [],
	},
};

const StoreContext = React.createContext({
	initialStoreState,
	setStore: () => null,
});

const StoreContextProvider = ({children}) => {
	const [store, setStore] = useState(initialStoreState);
	const [initStore, setInitStore] = useState(false);

	useEffect(() => {
		if (initStore === false) {
			const initializeCheckout = async () => {
				// Check for an existing cart.
				const isBrowser = typeof window !== 'undefined';
				const existingCheckoutId = isBrowser
					? localStorage.getItem(SHOPIFY_CHECKOUT_STORAGE_KEY)
					: null;

				if (existingCheckoutId) {
					try {
						const checkout = await fetchCheckout(
							store,
							existingCheckoutId,
						);
						// Make sure this cart hasn’t already been purchased.
						if (!checkout.completedAt) {
							setCheckoutInState(checkout, setStore);
							return;
						}
					} catch (e) {
						localStorage.setItem(
							SHOPIFY_CHECKOUT_STORAGE_KEY,
							null,
						);
					}
				}

				const newCheckout = await createNewCheckout(store);
				setCheckoutInState(newCheckout, setStore);
			};
			initCustomer(setStore);
			initializeCheckout();
			setInitStore(true);
		}
	}, [store, setStore, store.client.checkout, initStore]);

	return (
		<StoreContext.Provider
			value={{
				store,
				setStore,
			}}
		>
			{children}
		</StoreContext.Provider>
	);
};

function useStoreContext() {
	const {store} = useContext(StoreContext);
	return store;
}

const createNewCheckout = store => {
	return store.client.checkout.create();
};

const fetchCheckout = (store, id) => {
	return store.client.checkout.fetch(id);
};

const setCheckoutInState = (checkout, setStore) => {
	const isBrowser = typeof window !== 'undefined';
	if (isBrowser) {
		localStorage.setItem(SHOPIFY_CHECKOUT_STORAGE_KEY, checkout.id);
	}

	setStore(prevState => {
		return {...prevState, checkout};
	});
};

const initCustomer = setStore => {
	const customerEmail = cookie.get('customer_email');
	const customerToken = cookie.get('customer_token');
	const customerName = cookie.get('customer_firstName');

	if (customerEmail && customerToken && customerName) {
		setStore(prevState => {
			return {...prevState, customerEmail, customerToken, customerName};
		});
	}
};

function useCartCount() {
	const {
		store: {checkout},
	} = useContext(StoreContext);

	let count = 0;
	if (checkout.lineItems) {
		count = checkout.lineItems.reduce(
			(runningTotal, item) => item.quantity + runningTotal,
			0,
		);
	}

	return count;
}

function useCartTotals() {
	const {
		store: {checkout},
	} = useContext(StoreContext);

	const tax = checkout.totalTaxV2
		? `${Number(checkout.totalTaxV2.amount).toFixed(2)}`
		: '-';
	const total = checkout.totalPriceV2
		? `${Number(checkout.totalPriceV2.amount).toFixed(2)}`
		: '-';

	return {
		tax,
		total,
	};
}

function useCartItems() {
	const {
		store: {checkout},
	} = useContext(StoreContext);

	return checkout.lineItems;
}

function useCartAttributes() {
	const {
		store: {checkout},
	} = useContext(StoreContext);

	return checkout.customAttributes;
}

function useCustomer() {
	const {
		store: {customerEmail, customerName, customerToken},
	} = useContext(StoreContext);

	return {customerEmail, customerName, customerToken};
}

function useAddItemToCart() {
	const {
		store: {checkout, client},
		setStore,
	} = useContext(StoreContext);

	async function addItemToCart(variantId, quantity, attributes) {
		if (variantId === '' || !quantity) {
			console.error('variant id and quantity are required.');
			return;
		}

		setStore(prevState => {
			return {...prevState, isAdding: true};
		});

		const checkoutId = checkout.id;
		const lineItemsToAdd = [
			{variantId, quantity, customAttributes: attributes},
		];

		const newCheckout = await client.checkout.addLineItems(
			checkoutId,
			lineItemsToAdd,
		);

		setStore(prevState => {
			return {
				...prevState,
				checkout: newCheckout,
				cartIsOpen: true,
				isAdding: false,
			};
		});
	}

	return addItemToCart;
}

function useAddItemsToCart() {
	const {
		store: {checkout, client},
		setStore,
	} = useContext(StoreContext);

	async function addItemsToCart(items) {
		if (!items.length) {
			console.error('items are required.');
			return;
		}

		setStore(prevState => {
			return {...prevState, isAdding: true};
		});

		const checkoutId = checkout.id;

		const newCheckout = await client.checkout.addLineItems(
			checkoutId,
			items,
		);

		setStore(prevState => {
			return {
				...prevState,
				checkout: newCheckout,
				cartIsOpen: true,
				isAdding: false,
			};
		});
	}

	return addItemsToCart;
}

function useRemoveItemFromCart() {
	const {
		store: {checkout},
		setStore,
	} = useContext(StoreContext);

	async function removeItemFromCart(itemId) {
		const newCheckout = await client.checkout.removeLineItems(checkout.id, [
			itemId,
		]);

		setStore(prevState => {
			return {...prevState, checkout: newCheckout};
		});
	}

	return removeItemFromCart;
}

function useUpdateItemsFromCart() {
	const {
		store: {checkout},
		setStore,
	} = useContext(StoreContext);

	async function updateItemsFromCart(items) {
		items = [].concat(items);
		const newCheckout = await client.checkout.updateLineItems(
			checkout.id,
			items,
		);

		setStore(prevState => {
			return {...prevState, checkout: newCheckout};
		});
	}

	return updateItemsFromCart;
}

function useUpdateCheckoutAttributes() {
	const {
		store: {checkout},
		setStore,
	} = useContext(StoreContext);

	async function updateCheckoutAttributes(input) {
		const newCheckout = await client.checkout.updateAttributes(
			checkout.id,
			input,
		);

		setStore(prevState => {
			return {...prevState, checkout: newCheckout};
		});
	}

	return updateCheckoutAttributes;
}

function useCheckout() {
	const {
		store: {checkout},
	} = useContext(StoreContext);
	return () => {
		window.location.href = checkout.webUrl;
	};
}

function useSetPage() {
	const {setStore} = useContext(StoreContext);
	async function setPage(page) {
		setStore(prevState => {
			return {...prevState, page};
		});
	}
	return setPage;
}

function useToggleCart() {
	const {
		store: {cartIsOpen},
		setStore,
	} = useContext(StoreContext);

	async function toggleCart() {
		setStore(prevState => {
			return {
				...prevState,
				cartIsOpen: !cartIsOpen,
			};
		});
	}

	return toggleCart;
}

function useToggleMenu() {
	const {
		store: {menuIsOpen},
		setStore,
	} = useContext(StoreContext);

	function toggleMenu() {
		setStore(prevState => {
			return {...prevState, menuIsOpen: !menuIsOpen};
		});
	}

	return toggleMenu;
}

function useToggleSubmenu() {
	const {
		store: {submenuIsOpen},
		setStore,
	} = useContext(StoreContext);

	function toggleSubmenu() {
		setStore(prevState => {
			return {...prevState, submenuIsOpen: !submenuIsOpen};
		});
	}

	return toggleSubmenu;
}

function useSetActiveSubmenu() {
	const {
		store: {activeSubmenu},
		setStore,
	} = useContext(StoreContext);

	async function setActiveSubmenu(submenu) {
		setStore(prevState => {
			return {...prevState, activeSubmenu: submenu};
		});
	}

	return setActiveSubmenu;
}

function useToggleSearch() {
	const {
		store: {searchIsOpen},
		setStore,
	} = useContext(StoreContext);

	async function toggleSearch() {
		setStore(prevState => {
			return {
				...prevState,
				searchIsOpen: !searchIsOpen,
			};
		});
	}

	return toggleSearch;
}

function useEnableNoticeBar() {
	const {setStore} = useContext(StoreContext);

	function enableNoticeBar() {
		setStore(prevState => {
			return {...prevState, noticeBarEnabled: true};
		});
	}

	return enableNoticeBar;
}

function useRevealNoticeBar() {
	const {setStore} = useContext(StoreContext);

	function revealNoticeBar() {
		setStore(prevState => {
			return {...prevState, noticeBarVisible: true};
		});
	}

	return revealNoticeBar;
}

function useHideNoticeBar() {
	const {setStore} = useContext(StoreContext);

	function hideNoticeBar() {
		setStore(prevState => {
			return {...prevState, noticeBarVisible: false};
		});
	}

	return hideNoticeBar;
}

function useSetNoticeBarHeight() {
	const {
		store: {noticeBarHeight},
		setStore,
	} = useContext(StoreContext);

	async function setNoticeBarHeight(height) {
		setStore(prevState => {
			return {...prevState, noticeBarHeight: height};
		});
	}

	return setNoticeBarHeight;
}

function useDismissNoticeBar() {
	const {setStore} = useContext(StoreContext);

	function dismissNoticeBar() {
		setStore(prevState => {
			return {
				...prevState,
				noticeBarVisible: false,
				noticeBarDismissed: true,
			};
		});
	}

	return dismissNoticeBar;
}

export {
	client,
	StoreContextProvider,
	useAddItemToCart,
	useAddItemsToCart,
	useStoreContext,
	useCustomer,
	useCartCount,
	useCartItems,
	useCartTotals,
	useCartAttributes,
	useRemoveItemFromCart,
	useUpdateItemsFromCart,
	useUpdateCheckoutAttributes,
	useCheckout,
	useSetPage,
	useToggleCart,
	useToggleMenu,
	useToggleSubmenu,
	useSetActiveSubmenu,
	useToggleSearch,
	useEnableNoticeBar,
	useRevealNoticeBar,
	useHideNoticeBar,
	useSetNoticeBarHeight,
	useDismissNoticeBar,
};
